import React, { ReactElement } from 'react';
import ErrorsWrapper from '../../../../../shared/uibuilder/form/input/layout/ErrorsWrapper';
import RelativeRow, { Cell, EmptyCell } from '../../../../../shared/web/uitheme/form/RelativeRow';

import styles from '../../../../../plans/tasks/CreateUpdate/TransactionParties.module.scss';
import BriefcaseCheckbox from '../../../../../plans/tasks/CreateUpdate/BriefcaseCheckbox';
import Checkbox from '../../../../../shared/uibuilder/form/input/Checkbox';
import { OptInStatus, PersonPhoneNumber } from '../../../../../people/peopleService';
import LoadingAnimation from '../../../../../shared/LoadingAnimation';

export type Choice = {
  id: string
  name: string | ReactElement
  title?: string
  isAssignable: boolean
  isAssigned: boolean
  isSmsRecipient: boolean,
  phoneNumberForSms?: PersonPhoneNumber
}

export interface SmsTransactionPartiesInputLayoutProps {
  choices: Choice[]
  isDisabled: boolean
  numberOfSelectedOptions: number,
  onAssignmentChangedCallback: (id: string) => void,
  onSmsRecipientChangedCallback: (id: string) => void,
}

function WarningIcon(props: {
  text: string
}) {
  return (
    <span className={styles.warning} title={props.text}>&nbsp;</span>
  );
}

function OptInStatusWidget(
  props: {
    phoneNumberForSms?: PersonPhoneNumber
  },
) {
  if (!props.phoneNumberForSms) {
    return (<WarningIcon text="No mobile number available for SMS delivery." />);
  } else if (props.phoneNumberForSms.optInStatus !== OptInStatus.CONSENT_RECEIVED) {
    let message;

    switch (props.phoneNumberForSms.optInStatus) {
      case OptInStatus.NOT_SENT:
      case OptInStatus.WAITING_FOR_CONSENT:
        message = 'Recipient has not yet confirmed opt-in for SMS updates.';
        break;
      case OptInStatus.OPTED_OUT:
        message = 'Recipient has chosen to opt-out of SMS messages.';
        break;
      default:
        message = '';
    }

    if (message) {
      return (
        <WarningIcon text={message} />
      );
    } else {
      return null;
    }
  } else {
    return null;
  }
}

export default function SmsTransactionPartiesInputLayout(
  props: SmsTransactionPartiesInputLayoutProps,
) {
  const title = `Transaction Parties (${props.numberOfSelectedOptions})`;
  return (
    <ErrorsWrapper name="smsRecipients">
      <ErrorsWrapper name="transactionParties">
        <div className={styles.block}>
          <h3>
            {title}
          </h3>
          {!!props.choices.length && (
            <div className={styles.table}>
              <RelativeRow
                weights={[6, 1, 5]}
                rowClassName={styles.tableHead}
                cellClassName={styles.cell}
              >
                <Cell>
                  Assigned To:
                </Cell>
                <EmptyCell />
                <Cell centered>
                  Receive SMS:
                </Cell>
              </RelativeRow>
              <div className={styles.tableBody}>
                {props.choices.map(
                  (choice) => {
                    return (
                      <RelativeRow
                        key={`choice-${choice.id}`}
                        weights={[6, 1, 5]}
                        rowClassName={styles.row}
                        cellClassName={styles.cell}
                      >
                        {choice.isAssignable ? (
                          <BriefcaseCheckbox
                            title={choice.title}
                            source={`assigned.${choice.id}`}
                            label={choice.name}
                            value={choice.isAssigned}
                            isDisabled={props.isDisabled}
                            onChangeCallback={() => {
                              props.onAssignmentChangedCallback(choice.id);
                            }}
                          />
                        ) : (
                          <Cell className={styles.noAssignment}>
                            <span title={choice.title}>{choice.name}</span>
                          </Cell>
                        )}
                        <EmptyCell />
                        <Cell centered className={styles.checkboxCell}>
                          <Checkbox
                            source={`receiveSms.${choice.id}`}
                            label=""
                            value={choice.isSmsRecipient}
                            onChangeCallback={() => {
                              props.onSmsRecipientChangedCallback(choice.id);
                            }}
                          />
                          <OptInStatusWidget phoneNumberForSms={choice.phoneNumberForSms} />
                        </Cell>
                      </RelativeRow>
                    );
                  },
                )}
              </div>
            </div>
          )}
          {!props.choices.length && (<LoadingAnimation />)}
        </div>
      </ErrorsWrapper>
    </ErrorsWrapper>
  );
}
